import React, { useEffect, useState } from 'react';
import caseService from '../../services/caseService';
import api from '../../services/api';

// Staff case creation is the backfill/exception lane — the intake pipeline gate
// remains the canonical path for new engagements. The client is associated via
// a tenant-scoped contact picker (search by name/email/phone) with inline "New
// contact" creation; no email-fishing.
const CreateCaseModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    case_name: '',
    practice_area: '',
    description: '',
    status: 'active',
    billable: true,
    court_case_number: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Contact picker
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Inline new-contact creation
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [creatingContact, setCreatingContact] = useState(false);

  useEffect(() => {
    if (selectedContact || contactSearch.trim().length < 2) {
      setContactResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await api.get('/contacts/', { params: { search: contactSearch.trim(), limit: 15 } });
        const rows = Array.isArray(r.data) ? r.data : (r.data.contacts || []);
        setContactResults(rows);
      } catch (e) {
        setContactResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [contactSearch, selectedContact]);

  const contactLine = (c) =>
    [c.contact_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
     c.primary_email_address, c.primary_phone_number].filter(Boolean).join(' — ');

  const handleCreateContact = async () => {
    if (!newContact.first_name.trim() || !newContact.last_name.trim()) {
      setError('New contact needs first and last name');
      return;
    }
    if (!newContact.email.trim() && !newContact.phone.trim()) {
      setError('New contact needs an email or a phone number');
      return;
    }
    try {
      setCreatingContact(true);
      setError(null);
      const payload = {
        contact_type: 'Client',
        first_name: newContact.first_name.trim(),
        last_name: newContact.last_name.trim(),
        is_client: true,
        client_status: 'active',
      };
      if (newContact.email.trim()) payload.primary_email_address = newContact.email.trim();
      if (newContact.phone.trim()) payload.primary_phone_number = newContact.phone.trim();
      const r = await api.post('/contacts/', payload);
      const created = r.data.contact || r.data;
      setSelectedContact({
        id: created.id,
        contact_name: created.contact_name || `${payload.first_name} ${payload.last_name}`,
        primary_email_address: payload.primary_email_address,
        primary_phone_number: payload.primary_phone_number,
      });
      setShowNewContact(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create contact');
    } finally {
      setCreatingContact(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContact) {
      setError('Select a client contact (or create one inline)');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await caseService.createCase({
        ...formData,
        client_contact_id: selectedContact.id,
        client_name: selectedContact.contact_name,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const practiceAreas = [
    'Business Law',
    'Family Law',
    'Criminal Law',
    'Personal Injury',
    'Real Estate',
    'Estate Planning',
    'Immigration',
    'Employment Law',
    'Intellectual Property',
    'Tax Law',
    'Collections & Debt',
    'Injunctions',
    'Appeals',
    'Dependency',
    'Other',
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-20">
      <div className="relative mx-auto p-6 w-full max-w-2xl shadow-2xl rounded-xl bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 border border-virgo-teal-700/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Create New Case</h3>
          <button
            onClick={onClose}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-600/50 rounded-lg p-3">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Case Name */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Case Name *
            </label>
            <input
              type="text"
              name="case_name"
              required
              value={formData.case_name}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="e.g., Smith v. Jones - Contract Dispute"
            />
          </div>

          {/* Client contact picker */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Client *
            </label>
            {selectedContact ? (
              <div className="flex items-center justify-between px-3 py-2 bg-virgo-navy-600 border border-cyan-500/50 rounded-lg">
                <span className="text-white text-sm truncate">{contactLine(selectedContact)}</span>
                <button
                  type="button"
                  onClick={() => { setSelectedContact(null); setContactSearch(''); }}
                  className="text-virgo-teal-400 hover:text-white ml-2"
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Search contacts by name, email, or phone…"
                />
                {(contactResults.length > 0 || searching) && (
                  <div className="mt-1 max-h-48 overflow-y-auto bg-virgo-navy-800 border border-virgo-teal-700/50 rounded-lg divide-y divide-virgo-navy-600">
                    {searching && <p className="px-3 py-2 text-sm text-virgo-teal-500">Searching…</p>}
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedContact(c)}
                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-virgo-navy-600 transition-colors"
                      >
                        {contactLine(c)}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setShowNewContact(!showNewContact); setError(null); }}
                  className="mt-2 text-sm text-cyan-400 hover:text-cyan-300"
                >
                  {showNewContact ? '▾ New contact' : '▸ New contact'}
                </button>
                {showNewContact && (
                  <div className="mt-2 p-3 bg-virgo-navy-800/60 border border-virgo-teal-700/30 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={newContact.first_name}
                        onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                        placeholder="First name *"
                        className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm"
                      />
                      <input
                        type="text"
                        value={newContact.last_name}
                        onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                        placeholder="Last name *"
                        className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm"
                      />
                      <input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="Email"
                        className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm"
                      />
                      <input
                        type="tel"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        placeholder="Phone"
                        className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm"
                      />
                    </div>
                    <p className="text-xs text-virgo-teal-500">Email or phone required.</p>
                    <button
                      type="button"
                      onClick={handleCreateContact}
                      disabled={creatingContact}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50"
                    >
                      {creatingContact ? 'Creating…' : 'Create & select'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Practice Area */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Practice Area
            </label>
            <select
              name="practice_area"
              value={formData.practice_area}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">Select practice area...</option>
              {practiceAreas.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </div>

          {/* Court Case Number */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Court Case Number
            </label>
            <input
              type="text"
              name="court_case_number"
              value={formData.court_case_number}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="e.g., 2025-CV-12345"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Brief description of the case..."
            />
          </div>

          {/* Status and Billable */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="on_hold">On Hold</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="billable"
                checked={formData.billable}
                onChange={handleChange}
                className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 bg-virgo-navy-600 border-virgo-teal-700/50 rounded"
              />
              <label className="ml-2 block text-sm text-virgo-teal-300">
                Billable Case
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-virgo-navy-600">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 shadow-lg shadow-cyan-500/30 transition-all"
            >
              {loading ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCaseModal;
